import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import { FaMicrophone, FaArrowLeft, FaPhone, FaComments, FaUser, FaLightbulb, FaPaperPlane } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import CharacterBubble, { useRandomCharacter } from '../components/CharacterBubble';

// Conseils pour les interviews
const interviewTips = [
  "Prépare tes questions à l'avance, mais reste flexible !",
  "Écoute attentivement les réponses pour rebondir.",
  "Les questions ouvertes génèrent des réponses plus riches.",
  "Commence par des questions faciles pour mettre à l'aise.",
  "N'aie pas peur des silences, ils font parler !",
  "Reformule pour vérifier ta compréhension.",
  "Les questions courtes sont souvent les meilleures."
];

function InterviewPage() {
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [interviewEnded, setInterviewEnded] = useState(false);
  const [result, setResult] = useState(null);
  const [showTip, setShowTip] = useState(true);
  const [questionCount, setQuestionCount] = useState(0);
  const navigate = useNavigate();
  const { character: coach } = useRandomCharacter('interview');
  const [currentTip] = useState(interviewTips[Math.floor(Math.random() * interviewTips.length)]);

  useEffect(() => {
    fetchScenarios();
  }, []);

  const fetchScenarios = async () => {
    try {
      const response = await api.get('/interview/scenarios');
      setScenarios(response.data);
    } catch (error) {
      console.error('Error fetching scenarios:', error);
    } finally {
      setLoading(false);
    }
  };

  const startInterview = async (scenario) => {
    try {
      const response = await api.post('/interview/start', null, {
        params: { scenario_id: scenario.id }
      });
      setSelectedScenario(scenario);
      setAttemptId(response.data.attemptId);
      setMessages([{
        text: `Bonjour, je suis ${scenario.personaName}. ${response.data.message}`,
        isUser: false
      }]);
    } catch (error) {
      console.error('Error starting interview:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!currentMessage.trim() || sending) return;

    const userMessage = currentMessage;
    setCurrentMessage('');
    setSending(true);

    // Add user message
    const newMessages = [...messages, { text: userMessage, isUser: true }];
    setMessages(newMessages);

    try {
      const response = await api.post('/interview/message', {
        scenarioId: selectedScenario.id,
        message: userMessage,
        messageIndex: Math.floor(newMessages.filter(m => m.isUser).length - 1)
      });

      // Add AI response
      setMessages([...newMessages, { text: response.data.response, isUser: false }]);

      if (response.data.interviewEnded) {
        setInterviewEnded(true);
        setResult({
          score: response.data.score,
          feedback: response.data.feedback
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const resetInterview = () => {
    setSelectedScenario(null);
    setAttemptId(null);
    setMessages([]);
    setInterviewEnded(false);
    setResult(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8F4E3' }}>
        <div className="onomatopoeia">LOADING...</div>
      </div>
    );
  }

  const getModeIcon = (mode) => {
    switch(mode) {
      case 'phone': return <FaPhone />;
      case 'messaging': return <FaComments />;
      default: return <FaUser />;
    }
  };

  const getModeLabel = (mode) => {
    switch(mode) {
      case 'phone': return 'Téléphone';
      case 'messaging': return 'Messagerie';
      default: return 'En personne';
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#F8F4E3' }}>
      <header className="border-b-4 border-black" style={{ background: '#62B6CB' }}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <button
            onClick={() => { resetInterview(); navigate('/dashboard'); }}
            className="flex items-center space-x-2 comic-button bg-white text-black hover:bg-gray-100"
            data-testid="back-button"
          >
            <FaArrowLeft className="inline" /> 
            <span className="hidden sm:inline">{selectedScenario ? 'Quitter Interview' : 'Retour Dashboard'}</span>
            <span className="sm:hidden">Retour</span>
          </button>
          <h1 className="text-2xl md:text-3xl comic-title text-white">
            <FaMicrophone className="inline mr-2" />
            Simulation Interview
          </h1>
          {questionCount > 0 && (
            <div className="bg-white px-4 py-2 rounded-lg border-2 border-black">
              <span className="font-bold">{questionCount} questions</span>
            </div>
          )}
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Tip du coach Interview */}
        <AnimatePresence>
          {showTip && coach && !selectedScenario && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-4xl mx-auto mb-6"
            >
              <div className="speech-bubble p-4">
                <div className="flex items-start space-x-3">
                  <img
                    src={coach.image}
                    alt={coach.name}
                    className="w-14 h-14 object-contain rounded-full border-2 border-black bg-white"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <p className="font-bold comic-text" style={{ color: '#1B4965' }}>{coach.name} :</p>
                      <button 
                        onClick={() => setShowTip(false)}
                        className="text-gray-400 hover:text-gray-600 text-sm"
                      >✕</button>
                    </div>
                    <p className="text-gray-700">
                      <FaLightbulb className="inline text-yellow-500 mr-1" />
                      {currentTip}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {!selectedScenario ? (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="max-w-6xl mx-auto"
          >
            <h2 className="comic-title text-3xl mb-6 text-center" style={{ color: '#1B4965' }}>
              Choisis un scénario
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {scenarios.map((scenario) => (
                <motion.div
                  key={scenario.id}
                  whileHover={{ scale: 1.05 }}
                  className="comic-panel cursor-pointer hover:bg-yellow-50"
                  onClick={() => startInterview(scenario)}
                  data-testid={`scenario-${scenario.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="comic-badge bg-blue-500 text-white text-sm">
                      {getModeLabel(scenario.mode)}
                    </span>
                    <span className="text-2xl">{getModeIcon(scenario.mode)}</span>
                  </div>
                  
                  <h3 className="comic-text text-xl mb-2">{scenario.personaName}</h3>
                  <p className="text-sm mb-3">{scenario.context}</p>
                  
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: scenario.difficulty }).map((_, i) => (
                      <span key={i} className="text-yellow-500">⭐</span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ) : interviewEnded && result ? (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="max-w-2xl mx-auto comic-panel text-center"
          >
            <div className="onomatopoeia mb-6">
              {result.score >= 80 ? 'EXCELLENT!' : result.score >= 60 ? 'BIEN!' : 'CONTINUE!'}
            </div>
            
            <div className="text-6xl mb-4" style={{ color: '#62B6CB' }}>
              {result.score}/100
            </div>

            <div className="speech-bubble mb-6" data-testid="result-feedback">
              <p className="font-bold comic-text text-lg mb-2">Super Reporter évalue:</p>
              <p className="text-base">{result.feedback}</p>
            </div>

            <div className="space-x-4">
              <button
                onClick={resetInterview}
                className="comic-button comic-button-navy"
                data-testid="try-another-button"
              >
                Autre Interview
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="comic-button bg-gray-200"
              >
                Dashboard
              </button>
            </div>
          </motion.div>
        ) : (
          <div className="max-w-4xl mx-auto">
            {/* Scenario Info */}
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="comic-panel mb-4"
            >
              <div className="flex items-start space-x-4">
                <div className="text-5xl">{getModeIcon(selectedScenario.mode)}</div>
                <div>
                  <h3 className="comic-text text-2xl mb-2">{selectedScenario.personaName}</h3>
                  <p className="text-sm mb-2">{selectedScenario.personaDescription}</p>
                  <p className="text-sm text-gray-600"><strong>Contexte:</strong> {selectedScenario.context}</p>
                  <div className="mt-2">
                    <span className="comic-badge bg-blue-500 text-white text-xs">
                      {getModeLabel(selectedScenario.mode)}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Messages */}
            <div className="comic-panel mb-4 h-96 overflow-y-auto" data-testid="messages-container">
              <div className="space-y-4">
                {messages.map((msg, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ x: msg.isUser ? 50 : -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    className={`flex ${msg.isUser ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-md p-3 border-3 border-black ${
                        msg.isUser 
                          ? 'bg-yellow-100 rounded-lg' 
                          : 'bg-white speech-bubble'
                      }`}
                      data-testid={msg.isUser ? 'user-message' : 'ai-message'}
                    >
                      <p className="text-sm">{msg.text}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Input */}
            {!interviewEnded && (
              <form onSubmit={sendMessage} className="flex space-x-2" data-testid="message-form">
                <input
                  type="text"
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  placeholder="Tape ta question..."
                  className="flex-1 input-comic"
                  disabled={sending}
                  data-testid="message-input"
                />
                <button
                  type="submit"
                  disabled={sending || !currentMessage.trim()}
                  className="comic-button comic-button-red"
                  data-testid="send-message-button"
                >
                  {sending ? 'ENVOI...' : 'ENVOYER'}
                </button>
              </form>
            )}

            <p className="text-center text-sm mt-2 text-gray-600">
              Questions restantes: {10 - Math.floor(messages.filter(m => m.isUser).length)}/10
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default InterviewPage;
